// ─────────────────────────────────────────────────────────────────────────────
// El PAPEL de los documentos de Nómina: los estilos y el HTML del recibo de
// pago y del 1099-NEC.
//
// Está fuera del componente por dos razones:
//   1. Se puede PROBAR. Es JavaScript pelado, sin React, así que las pruebas
//      del backend lo importan y comprueban el documento de verdad — no un
//      trozo de texto del componente.
//   2. Se puede VER antes de mandarlo. `scripts/muestra-de-nomina.mjs` arma un
//      recibo de ejemplo con este mismo código, así que la muestra que se
//      revisa es literalmente el papel que va a salir.
//
// Lissette, 2026-09-03, queja 62: *"pásalo con división y un cuadro bien
// bonito, está horrible"*. El documento ahora es un CUADRO: un marco alrededor
// de todo el papel, con la cabecera dentro, y los Ingresos y las Deducciones
// en dos columnas separadas por una DIVISIÓN vertical — que es como se lee un
// recibo de pago de verdad. El PAGO NETO cierra el cuadro abajo, a lo ancho.
// ─────────────────────────────────────────────────────────────────────────────

/** Escapa el texto del usuario antes de meterlo en el HTML de impresión. */
export const esc = (v) => {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

export const money = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return '$0.00';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Siempre en-US y con el día LOCAL: `toISOString()` corre el día una zona
// horaria hacia atrás y el recibo saldría con la fecha de ayer.
export const fmtDate = (iso) => iso
  ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  : '—';

// ── Los estilos del papel, en ATRIBUTOS y no en una hoja ─────────────────────
//
// AQUI ESTA EL FALLO QUE SE COMIO EL DISENO (2026-09-03, queja 62 otra vez).
// La primera version de este archivo metia todas las reglas en un bloque
// `<style>`. La CSP de la aplicacion es `style-src-elem 'self'`: un `<style>`
// escrito a mano queda BLOQUEADO — y en silencio, sin error en la consola. La
// ventana de impresion se abre con `window.open('')`, o sea `about:blank`, que
// HEREDA la CSP de la aplicacion, asi que el recibo salia sin una sola regla:
// el logo a tamano natural ocupando dos paginas y el resto como texto suelto.
//
// La casa YA lo sabia. La Ficha del cliente (`ClientDetail.jsx`) y el Centro
// de Ayuda (`HelpCenterView.jsx`) llevan meses con el comentario escrito y con
// la solucion puesta: TODO en atributos `style="..."`, que la misma CSP SI
// permite (`style-src-attr 'unsafe-inline'`). Este archivo se escribio sin
// mirar ninguna de las dos. Es la regla de oro 125 por tercera vez.
//
// CONSECUENCIAS DE NO TENER HOJA, y como se resuelve cada una:
//   - No hay `@media print`. Lo que hacia falta al imprimir se pone directo en
//     el elemento: `break-inside:avoid` y `print-color-adjust:exact` (sin esto
//     ultimo el navegador imprime los fondos en blanco y el cuadro se pierde).
//   - No hay `nth-child(even)`. El rayado de las filas se decide al generarlas.
//   - El fondo gris de pantalla se va: el papel es blanco siempre, que es como
//     se imprime de todos modos.

/** `print-color-adjust`: sin esto los fondos NO se imprimen y el cuadro se pierde. */
const CADJ = '-webkit-print-color-adjust:exact;print-color-adjust:exact';

/**
 * Cada regla del papel, ya como texto de atributo `style`.
 *
 * Se exporta para que las pruebas comprueben el diseno en un solo sitio, pero
 * lo que manda es el HTML que sale: ahi es donde se ve si una regla se aplico.
 */
export const E = {
  body: `font-family:'Segoe UI',system-ui,sans-serif;color:#0f172a;padding:26px;line-height:1.5;font-size:12.5px;background:#fff;margin:0;${CADJ}`,
  // EL CUADRO: el marco que rodea todo el papel, y que NO se quita al imprimir.
  hoja: `max-width:760px;margin:0 auto;background:#fff;border:1px solid #94a3b8;border-radius:10px;overflow:hidden;${CADJ}`,
  cuerpo: 'padding:18px 22px 22px',
  // Cabecera: la marca de LA AGENCIA, no la nuestra.
  header: `display:flex;align-items:center;justify-content:space-between;gap:20px;padding:16px 22px;background:#f8fafc;border-bottom:2px solid #0f172a;${CADJ}`,
  brand: 'display:flex;align-items:center;gap:12px;min-width:0',
  // El tope del logo es lo que impedia que una imagen grande se comiera el
  // documento entero. Sin hoja de estilos no se aplicaba NINGUNO de los dos.
  brandImg: 'max-height:54px;max-width:200px;object-fit:contain;display:block',
  brandName: 'font-size:20px;font-weight:800;letter-spacing:-.01em;color:#0f172a;line-height:1.2',
  brandSub: 'font-size:11px;color:#475569;margin-top:3px;line-height:1.4;font-weight:600',
  doc: 'text-align:right;flex-shrink:0',
  tag: `display:inline-block;background:#0f172a;color:#fff;font-size:10px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;padding:6px 12px;border-radius:4px;${CADJ}`,
  when: 'font-size:10.5px;color:#64748b;margin-top:6px',
  h2: 'font-size:10.5px;color:#0f172a;margin:0 0 8px;text-transform:uppercase;letter-spacing:.1em;font-weight:800;border-left:3px solid #0ea5e9;padding-left:8px',
  // Tablas: rejilla completa y numeros alineados por la coma.
  table: 'width:100%;border-collapse:collapse;border:1px solid #cbd5e1',
  tableLimpia: 'width:100%;border-collapse:collapse;border:none',
  th: `background:#f1f5f9;text-align:left;padding:7px 10px;font-size:9.5px;color:#334155;text-transform:uppercase;letter-spacing:.07em;font-weight:800;border:1px solid #cbd5e1;${CADJ}`,
  thLimpia: `background:#f1f5f9;text-align:left;padding:7px 10px;font-size:9.5px;color:#334155;text-transform:uppercase;letter-spacing:.07em;font-weight:800;border:1px solid #cbd5e1;border-left:none;border-right:none;border-top:none;${CADJ}`,
  td: 'padding:7px 10px;border:1px solid #e2e8f0;font-size:12px;vertical-align:top',
  tdLimpia: 'padding:7px 10px;border:1px solid #e2e8f0;border-left:none;border-right:none;font-size:12px;vertical-align:top',
  // El rayado de las filas pares, que sin hoja hay que decidir al generarlas.
  zebra: `background:#f8fafc;${CADJ}`,
  right: 'text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap',
  bold: 'font-weight:700',
  vacio: 'color:#94a3b8',
  // Recuadros de datos.
  box: `border:1px solid #cbd5e1;border-radius:6px;padding:10px 12px;background:#fff;break-inside:avoid;${CADJ}`,
  boxLbl: 'font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#0ea5e9;font-weight:800;margin-bottom:5px;padding-bottom:4px;border-bottom:1px solid #e2e8f0',
  grid2: 'display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px',
  // LA DIVISION: Ingresos a la izquierda, Deducciones a la derecha.
  columnas: 'display:grid;grid-template-columns:1fr 1fr;border:1px solid #94a3b8;border-radius:8px 8px 0 0;overflow:hidden;margin-bottom:0;break-inside:avoid',
  // Cada columna es una pila: la tabla arriba y el subtotal empujado al fondo,
  // para que los dos totales queden a la MISMA altura aunque una columna tenga
  // mas renglones que la otra.
  columna: 'padding:12px 14px;display:flex;flex-direction:column',
  columnaDer: 'padding:12px 14px;display:flex;flex-direction:column;border-left:1px solid #94a3b8',
  subtotal: 'margin-top:auto;padding-top:9px;border-top:2px solid #cbd5e1;display:flex;align-items:baseline;justify-content:space-between;gap:14px',
  subtotalLbl: 'font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:#334155;font-weight:800',
  subtotalVal: 'font-size:14.5px;font-weight:800;color:#0f172a;font-variant-numeric:tabular-nums',
  // El numero que todo el mundo busca, cerrando el cuadro.
  neto: `display:flex;align-items:center;justify-content:space-between;gap:16px;border:1px solid #94a3b8;border-top:none;border-radius:0 0 8px 8px;background:#ecfdf5;padding:14px 18px;break-inside:avoid;${CADJ}`,
  netoLbl: 'font-size:11.5px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#065f46',
  netoVal: 'font-size:26px;font-weight:800;color:#047857;font-variant-numeric:tabular-nums;letter-spacing:-.01em',
  trasElCuadro: 'margin-top:16px',
  footer: 'margin-top:18px;padding-top:10px;border-top:1px solid #e2e8f0;font-size:9px;color:#94a3b8;display:flex;justify-content:space-between;gap:12px',
  disclaimer: `margin-top:12px;font-size:9px;color:#b45309;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:8px 10px;${CADJ}`,
};

/** El documento completo. Un solo sitio: la ventana de impresion y la muestra
 *  que se revisa salen del mismo molde. SIN bloque `<style>`: ver arriba. */
export function documentoCompleto(titulo, cuerpoHtml) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(titulo)}</title></head>`
    + `<body style="${E.body}"><div style="${E.hoja}">${cuerpoHtml}</div></body></html>`;
}

/**
 * Cabecera del documento con la marca de LA AGENCIA.
 *
 * Este papel no lo firma Deskte: lo entrega la agencia a su trabajador. Sin
 * logo cargado se pinta el NOMBRE, que sigue siendo su marca; y si tampoco hay
 * nombre —una cuenta a medio configurar— se cae a Deskte, que es mejor que una
 * cabecera vacía.
 *
 * `marca` viene de `marcaDeLaAgencia(currentUser)`: mira el logo de Mi Agencia
 * Y el de Mi Perfil, que es justo lo que faltaba (queja 62, 2026-09-03).
 */
export function cabeceraDeAgencia(es, marca, docLabel, dateStr) {
  const nombre = (marca && marca.nombre) || '';
  const logo = (marca && marca.logo) || '';
  // Con logo, el nombre va debajo en pequeño: un logotipo casi nunca se lee
  // como texto, y en un papel oficial tiene que constar quién lo emite.
  const bloqueDeMarca = logo
    ? `<img style="${E.brandImg}" src="${esc(logo)}" alt="${esc(nombre)}">${nombre ? `<div style="${E.brandSub}">${esc(nombre)}</div>` : ''}`
    : `<div style="${E.brandName}">${esc(nombre || 'Deskte')}</div>`;
  return `<div style="${E.header}">
    <div style="${E.brand}"><div>${bloqueDeMarca}</div></div>
    <div style="${E.doc}">
      <span style="${E.tag}">${esc(docLabel)}</span>
      <div style="${E.when}">${es ? 'Generado' : 'Generated'}: ${esc(dateStr)}</div>
    </div>
  </div>`;
}

export function payerBlock(es, payer) {
  const p = payer || {};
  const lines = [p.name, p.address, p.cityStateZip, p.phone, p.ein ? `EIN: ${p.ein}` : '']
    .filter(Boolean).map(esc).join('<br>');
  return `<div style="${E.box}"><div style="${E.boxLbl}">${es ? 'Pagador / Empresa' : 'Payer / Company'}</div>${lines || '—'}</div>`;
}

function pie(es, marca, emisor) {
  return `<div style="${E.footer}">
    <span>${esc((marca && marca.nombre) || '')}</span>
    <span>${es ? 'Emitido por' : 'Issued by'} ${esc(emisor || '')} · Deskte</span>
  </div>`;
}

/**
 * El recibo de pago.
 *
 * `d` son los datos del recibo: los que se están escribiendo ahora o los de
 * una fila del historial (queja 61). Es el MISMO HTML en los dos casos: dos
 * copias se separarían en cuanto alguien tocara una (E-138).
 */
export function cuerpoDelPaystub({ es, marca, payer, d, emisor }) {
  const dedus = Array.isArray(d.deductions) ? d.deductions : [];
  const totalDed = dedus.reduce((acc, x) => acc + (Number(x.amount) || 0), 0);
  const neto = (Number(d.gross) || 0) - totalDed;
  const recipLines = [d.name, d.address, d.cityStateZip, d.ssnLast4 ? `SSN: ***-**-${d.ssnLast4}` : '']
    .filter(Boolean).map(esc).join('<br>');
  const dedRows = dedus.length
    ? dedus.map((ded, i) => `<tr${i % 2 ? ` style="${E.zebra}"` : ''}><td style="${E.tdLimpia}">${esc(ded.label) || '—'}</td><td style="${E.tdLimpia};${E.right}">${money(ded.amount)}</td></tr>`).join('')
    : `<tr><td colspan="2" style="${E.tdLimpia};${E.vacio}">${es ? 'Sin deducciones' : 'No deductions'}</td></tr>`;
  return `${cabeceraDeAgencia(es, marca, es ? 'Recibo de Pago (Paystub)' : 'Pay Stub', fmtDate(d.payDate))}
    <div style="${E.cuerpo}">
      <div style="${E.grid2}">
        ${payerBlock(es, payer)}
        <div style="${E.box}"><div style="${E.boxLbl}">${es ? 'Trabajador' : 'Employee / Worker'}</div>${recipLines || '—'}</div>
      </div>
      <table style="${E.table};margin-bottom:14px">
        <tr><th style="${E.th}">${es ? 'Período de pago' : 'Pay period'}</th><th style="${E.th}">${es ? 'Fecha de pago' : 'Pay date'}</th></tr>
        <tr><td style="${E.td}">${fmtDate(d.periodStart)} — ${fmtDate(d.periodEnd)}</td><td style="${E.td}">${fmtDate(d.payDate)}</td></tr>
      </table>
      <div style="${E.columnas}">
        <section style="${E.columna}">
          <h2 style="${E.h2}">${es ? 'Ingresos' : 'Earnings'}</h2>
          <table style="${E.tableLimpia}">
            <tr><th style="${E.thLimpia}">${es ? 'Concepto' : 'Description'}</th><th style="${E.thLimpia};${E.right}">${es ? 'Monto' : 'Amount'}</th></tr>
            <tbody>
              <tr><td style="${E.tdLimpia}">${es ? 'Pago bruto' : 'Gross pay'}</td><td style="${E.tdLimpia};${E.right}">${money(d.gross)}</td></tr>
              ${d.ytdGross ? `<tr><td style="${E.tdLimpia}">${es ? 'Bruto acumulado (YTD)' : 'Year-to-date gross (YTD)'}</td><td style="${E.tdLimpia};${E.right}">${money(d.ytdGross)}</td></tr>` : ''}
            </tbody>
          </table>
          <div style="${E.subtotal}">
            <span style="${E.subtotalLbl}">${es ? 'Total ingresos' : 'Total earnings'}</span>
            <span style="${E.subtotalVal}">${money(d.gross)}</span>
          </div>
        </section>
        <section style="${E.columnaDer}">
          <h2 style="${E.h2}">${es ? 'Deducciones' : 'Deductions'}</h2>
          <table style="${E.tableLimpia}">
            <tr><th style="${E.thLimpia}">${es ? 'Concepto' : 'Description'}</th><th style="${E.thLimpia};${E.right}">${es ? 'Monto' : 'Amount'}</th></tr>
            <tbody>
              ${dedRows}
            </tbody>
          </table>
          <div style="${E.subtotal}">
            <span style="${E.subtotalLbl}">${es ? 'Total deducciones' : 'Total deductions'}</span>
            <span style="${E.subtotalVal}">${money(totalDed)}</span>
          </div>
        </section>
      </div>
      <div style="${E.neto}">
        <span style="${E.netoLbl}">${es ? 'Pago neto' : 'Net pay'}</span>
        <span style="${E.netoVal}">${money(neto)}</span>
      </div>
      ${d.notes ? `<div style="${E.trasElCuadro}"><h2 style="${E.h2}">${es ? 'Notas' : 'Notes'}</h2><div style="${E.box}">${esc(d.notes)}</div></div>` : ''}
      <div style="${E.disclaimer}">${es
        ? 'Documento generado manualmente. Los montos fueron ingresados por el usuario y no incluyen cálculo automático de impuestos. Verifique con su contador antes de usarlo con fines oficiales.'
        : 'Manually generated document. Amounts were entered by the user and do not include automatic tax calculation. Verify with your accountant before any official use.'}</div>
      ${pie(es, marca, emisor)}
    </div>`;
}

/**
 * El 1099-NEC (copia del destinatario).
 *
 * `delHistorial` marca los que se vuelven a armar desde el historial: esos NO
 * traen el TIN, porque ese número no se guarda en ninguna parte (decisión del
 * 2026-06-09) y no la cambia una comodidad de pantalla. En vez de dejar el
 * renglón en blanco —que se leería como que el documento salió así— lo dice.
 */
export function cuerpoDel1099({ es, marca, payer, d, emisor, delHistorial = false, hoy }) {
  const lineaTin = d.tin
    ? `TIN: ${esc(d.tin)}`
    : (delHistorial
      ? `<span style="color:#b45309">${es ? 'TIN no guardado — vuelve a escribirlo para reimprimirlo' : 'TIN not stored — retype it to reprint'}</span>`
      : '');
  const recipLines = [esc(d.name), esc(d.address), esc(d.cityStateZip), lineaTin].filter(Boolean).join('<br>');
  const conEstado = !!(d.stateName || d.stateIncome || d.stateTax);
  return `${cabeceraDeAgencia(es, marca, `Form 1099-NEC · ${es ? 'Año' : 'Tax Year'} ${esc(d.year)} · ${es ? 'Copia del Destinatario' : 'Recipient Copy'}`, fmtDate(hoy))}
    <div style="${E.cuerpo}">
      <div style="${E.grid2}">
        ${payerBlock(es, payer)}
        <div style="${E.box}"><div style="${E.boxLbl}">${es ? 'Destinatario (Recipient)' : 'Recipient'}</div>${recipLines || '—'}</div>
      </div>
      ${d.accountNumber ? `<div style="${E.box};margin-bottom:14px"><div style="${E.boxLbl}">${es ? 'Número de cuenta' : 'Account number'}</div>${esc(d.accountNumber)}</div>` : ''}
      <h2 style="${E.h2}">${es ? 'Compensación' : 'Compensation'}</h2>
      <table style="${E.table};margin-bottom:14px">
        <tr><th style="${E.th}">${es ? 'Casilla' : 'Box'}</th><th style="${E.th}">${es ? 'Descripción' : 'Description'}</th><th style="${E.th};${E.right}">${es ? 'Monto' : 'Amount'}</th></tr>
        <tbody>
          <tr><td style="${E.td};${E.bold}">1</td><td style="${E.td}">${es ? 'Compensación a no-empleado' : 'Nonemployee compensation'}</td><td style="${E.td};${E.right};${E.bold}">${money(d.box1)}</td></tr>
          <tr><td style="${E.td};${E.bold}">4</td><td style="${E.td}">${es ? 'Impuesto federal retenido' : 'Federal income tax withheld'}</td><td style="${E.td};${E.right}">${money(d.box4)}</td></tr>
        </tbody>
      </table>
      ${conEstado ? `
      <h2 style="${E.h2}">${es ? 'Información estatal' : 'State information'}</h2>
      <table style="${E.table};margin-bottom:14px">
        <tr><th style="${E.th}">${es ? 'Estado' : 'State'}</th><th style="${E.th}">${es ? 'No. ID estatal' : 'State ID no.'}</th><th style="${E.th};${E.right}">${es ? 'Impuesto estatal (Box 5)' : 'State tax withheld (Box 5)'}</th><th style="${E.th};${E.right}">${es ? 'Ingreso estatal (Box 7)' : 'State income (Box 7)'}</th></tr>
        <tbody>
          <tr><td style="${E.td}">${esc(d.stateName) || '—'}</td><td style="${E.td}">${esc(d.stateId) || '—'}</td><td style="${E.td};${E.right}">${money(d.stateTax)}</td><td style="${E.td};${E.right}">${money(d.stateIncome)}</td></tr>
        </tbody>
      </table>` : ''}
      <div style="${E.disclaimer}">${es
        ? 'Esta es la COPIA DEL DESTINATARIO del Formulario 1099-NEC, generada manualmente. La Copia A debe presentarse ante el IRS por separado (e-file vía IRIS o un servicio). Si presenta 10 o más formularios, el e-file es obligatorio. Verifique con su contador.'
        : 'This is the RECIPIENT COPY of Form 1099-NEC, manually generated. Copy A must be filed separately with the IRS (e-file via IRIS or a service). If filing 10+ returns, e-file is mandatory. Verify with your accountant.'}</div>
      ${pie(es, marca, emisor)}
    </div>`;
}
